/**
 * Quick Add to Cart Component
 * Handles direct add to cart for simple products and shows a popover for products with variants
 */

if (!customElements.get('quick-add-to-cart')) {
  customElements.define(
    'quick-add-to-cart',
    class QuickAddToCart extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        if (this._initialized) return;
        this._initialized = true;

        this.button = this.querySelector('button');
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        
        this.productId = this.dataset.productId;
        this.variantId = this.dataset.variantId;
        this.hasVariants = this.dataset.hasVariants === 'true';
        this.productUrl = this.dataset.productUrl;
        
        this.boundHandleOutsideClick = this.handleOutsideClick.bind(this);
        this.boundHandleEscape = this.handleEscape.bind(this);
        
        if (!this.button) return;
        this.init();
      }

      getPopover() {
        return this.querySelector('.quick-add-popover');
      }

      init() {
        this.button.addEventListener('click', this.handleButtonClick.bind(this));
        
        const popover = this.getPopover();
        if (popover) {
          popover.addEventListener('click', (e) => e.stopPropagation());
        }
      }

      handleButtonClick(e) {
        e.preventDefault();
        e.stopPropagation();

        if (this.hasVariants) {
          this.togglePopover();
        } else {
          this.addToCart(this.variantId);
        }
      }

      togglePopover() {
        const popover = this.getPopover();
        if (!popover) return;

        if (popover.classList.contains('active')) {
          this.closePopover();
        } else {
          this.openPopover();
        }
      }

      openPopover() {
        const popover = this.getPopover();
        if (!popover) return;

        // Close any other open popovers first
        document.querySelectorAll('.quick-add-popover.active').forEach(p => {
          if (p !== popover) {
            p.classList.remove('active');
          }
        });

        // If popover content hasn't been loaded yet, fetch it
        if (!popover.dataset.loaded) {
          this.loadPopoverContent();
        }
        
        popover.classList.add('active');
        this.button.setAttribute('aria-expanded', 'true');
        
        // Add event listeners for closing
        document.addEventListener('click', this.boundHandleOutsideClick);
        document.addEventListener('keydown', this.boundHandleEscape);
        
        // Position the popover
        this.positionPopover();
      }

      closePopover() {
        const popover = this.getPopover();
        if (!popover) return;
        popover.classList.remove('active');
        this.button.setAttribute('aria-expanded', 'false');
        
        document.removeEventListener('click', this.boundHandleOutsideClick);
        document.removeEventListener('keydown', this.boundHandleEscape);
      }

      handleOutsideClick(e) {
        if (!this.contains(e.target)) {
          this.closePopover();
        }
      }

      handleEscape(e) {
        if (e.key === 'Escape') {
          this.closePopover();
          this.button.focus();
        }
      }

      positionPopover() {
        const popover = this.getPopover();
        if (!popover) return;
        const buttonRect = this.button.getBoundingClientRect();
        const popoverRect = popover.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        // Reset position
        popover.style.left = '';
        popover.style.right = '';
        popover.style.bottom = '';
        popover.style.top = '';

        // Check if popover would overflow right
        if (buttonRect.left + popoverRect.width > viewportWidth - 20) {
          popover.style.right = '0';
          popover.style.left = 'auto';
        }

        // Check if popover would overflow bottom - if so, show above
        if (buttonRect.bottom + popoverRect.height > viewportHeight - 20) {
          popover.style.bottom = '100%';
          popover.style.top = 'auto';
          popover.style.marginBottom = '0.5rem';
        }
      }

      async loadPopoverContent() {
        const popover = this.getPopover();
        if (!popover) return;
        const contentContainer = popover.querySelector('.quick-add-popover__content');
        const loadingSpinner = popover.querySelector('.quick-add-popover__loading');
        
        if (loadingSpinner) loadingSpinner.classList.remove('hidden');

        try {
          const response = await fetch(this.productUrl);
          const html = await response.text();
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          
          // Get the product variants JSON
          const variantJson = doc.querySelector('[type="application/json"]');
          if (variantJson) {
            this.variants = JSON.parse(variantJson.textContent);
          }

          // Get product options
          const productElement = doc.querySelector('[data-section-type="product"]') || 
                                 doc.querySelector('section[id^="MainProduct-"]') ||
                                 doc.querySelector('.product');
          
          // Build the options UI
          this.buildOptionsUI(contentContainer, doc);
          
          popover.dataset.loaded = 'true';
        } catch (error) {
          console.error('Error loading product options:', error);
          contentContainer.innerHTML = '<p class="quick-add-popover__error">Unable to load options. Please try again.</p>';
        } finally {
          if (loadingSpinner) loadingSpinner.classList.add('hidden');
        }
      }

      buildOptionsUI(container, doc) {
        // Find product options from the page
        const optionsContainer = doc.querySelector('variant-radios') || doc.querySelector('variant-selects');
        
        if (!optionsContainer) {
          // No options found - shouldn't happen for variant products
          container.innerHTML = '<p>No options available</p>';
          return;
        }

        // Get variants JSON
        const variantsJson = optionsContainer.querySelector('[type="application/json"]');
        if (variantsJson) {
          this.variants = JSON.parse(variantsJson.textContent);
        }

        // Build simple select dropdowns for each option
        const fieldsets = optionsContainer.querySelectorAll('fieldset, .product-form__input');
        let optionsHtml = '<div class="quick-add-popover__options">';
        
        fieldsets.forEach((fieldset, index) => {
          const legend = fieldset.querySelector('legend, label');
          const optionName = legend ? legend.textContent.trim() : `Option ${index + 1}`;
          
          // Get option values
          const inputs = fieldset.querySelectorAll('input[type="radio"]');
          const select = fieldset.querySelector('select');
          
          optionsHtml += `<div class="quick-add-popover__option">
            <label class="quick-add-popover__label">${optionName}</label>
            <select class="quick-add-popover__select" data-option-index="${index}">`;
          
          if (inputs.length > 0) {
            inputs.forEach(input => {
              const value = input.value;
              const isChecked = input.checked;
              optionsHtml += `<option value="${value}" ${isChecked ? 'selected' : ''}>${value}</option>`;
            });
          } else if (select) {
            select.querySelectorAll('option').forEach(option => {
              optionsHtml += `<option value="${option.value}" ${option.selected ? 'selected' : ''}>${option.textContent}</option>`;
            });
          }
          
          optionsHtml += `</select></div>`;
        });
        
        optionsHtml += '</div>';
        
        // Add the add to cart button
        optionsHtml += `
          <div class="quick-add-popover__actions">
            <button type="button" class="quick-add-popover__add-button button button--primary">
              <div class="loading-overlay__spinner hidden">
                <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                  <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
                </svg>
              </div>
              <span class="quick-add-popover__add-text">Add to Cart</span>
              <span class="quick-add-popover__adding-text hidden">Adding...</span>
              <span class="quick-add-popover__added-text hidden">Added!</span>
              <span class="quick-add-popover__unavailable-text hidden">Unavailable</span>
            </button>
          </div>
        `;
        
        container.innerHTML = optionsHtml;
        
        // Add event listeners to selects
        const selects = container.querySelectorAll('.quick-add-popover__select');
        selects.forEach(select => {
          select.addEventListener('change', () => this.updateSelectedVariant());
        });
        
        // Add event listener to add button
        const addButton = container.querySelector('.quick-add-popover__add-button');
        addButton.addEventListener('click', () => this.handlePopoverAddToCart());
        
        // Set initial variant
        this.updateSelectedVariant();
      }

      updateSelectedVariant() {
        if (!this.variants) return;
        const popover = this.getPopover();
        if (!popover) return;
        
        const selects = popover.querySelectorAll('.quick-add-popover__select');
        const selectedOptions = Array.from(selects).map(select => select.value);
        
        // Find matching variant
        this.selectedVariant = this.variants.find(variant => {
          return variant.options.every((option, index) => option === selectedOptions[index]);
        });
        
        const addButton = popover.querySelector('.quick-add-popover__add-button');
        const addText = addButton.querySelector('.quick-add-popover__add-text');
        const unavailableText = addButton.querySelector('.quick-add-popover__unavailable-text');
        
        if (this.selectedVariant && this.selectedVariant.available) {
          addButton.disabled = false;
          addText.classList.remove('hidden');
          unavailableText.classList.add('hidden');
        } else {
          addButton.disabled = true;
          addText.classList.add('hidden');
          unavailableText.classList.remove('hidden');
        }
      }

      handlePopoverAddToCart() {
        if (!this.selectedVariant || !this.selectedVariant.available) return;
        
        const popover = this.getPopover();
        if (!popover) return;
        this.addToCart(this.selectedVariant.id, popover.querySelector('.quick-add-popover__add-button'));
      }

      async addToCart(variantId, buttonElement = this.button) {
        if (this.addingToCart || buttonElement.disabled) return;
        this.addingToCart = true;
        const wasDisabled = buttonElement.disabled;
        buttonElement.disabled = true;
        const spinner = buttonElement.querySelector('.loading-overlay__spinner');
        const buttonTextSpan = buttonElement.querySelector('span:not(.hidden):not(.icon-wrap)');
        const originalText = buttonTextSpan ? buttonTextSpan.textContent : '';
        const isPopoverButton = buttonElement.classList.contains('quick-add-popover__add-button');
        
        // Store original text for later
        buttonElement.dataset.originalText = originalText;
        
        buttonElement.setAttribute('aria-disabled', 'true');
        buttonElement.classList.add('loading');
        if (spinner) spinner.classList.remove('hidden');
        
        // Update button text to "Adding..."
        if (isPopoverButton) {
          buttonElement.querySelector('.quick-add-popover__add-text')?.classList.add('hidden');
          buttonElement.querySelector('.quick-add-popover__adding-text')?.classList.remove('hidden');
        } else if (buttonTextSpan) {
          buttonTextSpan.textContent = 'Adding...';
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', 1);
        
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        
        config.body = formData;

        try {
          const response = await fetch(`${routes.cart_add_url}`, config);
          const responseData = await response.json();

          if (responseData.status) {
            // Error occurred
            console.error('Error adding to cart:', responseData.description);
            this.showError(responseData.description || 'Could not add to cart');
            this.resetButtonState(buttonElement, isPopoverButton, buttonTextSpan);
            return;
          }

          // Success - show cart notification
          if (this.cart) {
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'quick-add-to-cart', productVariantId: variantId });
            }
            this.cart.renderContents(responseData);
          } else {
            // Fallback - redirect to cart
            window.location = window.routes.cart_url;
          }

          // Show "Added!" state
          this.showAddedState(buttonElement, isPopoverButton, buttonTextSpan);

          // Close popover if open (after a short delay to show Added state)
          const popoverEl = this.getPopover();
          if (this.hasVariants && popoverEl && popoverEl.classList.contains('active')) {
            setTimeout(() => this.closePopover(), 1000);
          }

        } catch (error) {
          console.error('Error adding to cart:', error);
          this.showError('Could not add to cart. Please try again.');
          // Reset button on error
          this.resetButtonState(buttonElement, isPopoverButton, buttonTextSpan);
        } finally {
          this.addingToCart = false;
          buttonElement.disabled = wasDisabled;
          buttonElement.removeAttribute('aria-disabled');
          buttonElement.classList.remove('loading');
          if (spinner) spinner.classList.add('hidden');
        }
      }

      showAddedState(buttonElement, isPopoverButton, buttonTextSpan) {
        if (isPopoverButton) {
          buttonElement.querySelector('.quick-add-popover__adding-text')?.classList.add('hidden');
          buttonElement.querySelector('.quick-add-popover__added-text')?.classList.remove('hidden');
          buttonElement.classList.add('added');
        } else if (buttonTextSpan) {
          buttonTextSpan.textContent = 'Added!';
          buttonElement.classList.add('added');
        }

        // Reset to original state after delay
        setTimeout(() => {
          this.resetButtonState(buttonElement, isPopoverButton, buttonTextSpan);
        }, 2000);
      }

      resetButtonState(buttonElement, isPopoverButton, buttonTextSpan) {
        buttonElement.classList.remove('added');
        
        if (isPopoverButton) {
          buttonElement.querySelector('.quick-add-popover__adding-text')?.classList.add('hidden');
          buttonElement.querySelector('.quick-add-popover__added-text')?.classList.add('hidden');
          buttonElement.querySelector('.quick-add-popover__add-text')?.classList.remove('hidden');
        } else if (buttonTextSpan) {
          const originalText = buttonElement.dataset.originalText || 'Add to Cart';
          buttonTextSpan.textContent = originalText;
        }
      }

      showError(message) {
        // Create a temporary error message
        const existingError = this.querySelector('.quick-add-error');
        if (existingError) existingError.remove();

        const errorEl = document.createElement('div');
        errorEl.className = 'quick-add-error';
        errorEl.textContent = message;
        this.appendChild(errorEl);

        setTimeout(() => {
          errorEl.remove();
        }, 3000);
      }
    }
  );
}
